import {
    ChangeRequestReviewStatus,
    GitBookUsers,
    type MergeRule,
    type MergeRulesEvaluationContext,
} from '@gitbook/api-client';
import { ExpressionRuntime } from '@gitbook/expr';
import { arrayOfAll } from '@gitbook/util-ts';

export type MergeRuleByName<Name extends MergeRule['rule']> = Extract<MergeRule, { rule: Name }>;

export type MergeRuleDefinition<Name extends MergeRule['rule']> = {
    /** Title for the rule */
    title: string;
    /** Description for the rule */
    description: string;
    /** Evaluate the rule against the given context */
    evaluate: (
        rule: MergeRuleByName<Name>,
        context: MergeRulesEvaluationContext
    ) => boolean | string;
    /** If true, if the rule evaluates to true, it'll directly allow the merge */
    bypass?: boolean;
};

export const ALL_MERGE_RULES = arrayOfAll<MergeRule['rule']>()([
    'require_at_least_one_review',
    'require_at_least_one_approved_review',
    'require_all_reviews_approved',
    'require_specific_reviewers',
    'require_one_of_specific_reviewers',
    'require_agent_review',
    'require_up_to_date_change_request',
    'require_change_request_subject',
    'require_change_request_description',
    'allow_bypass',
    'require_author_to_merge',
    'custom',
]);

export const MERGE_RULES: {
    [Name in MergeRule['rule']]: MergeRuleDefinition<Name>;
} = {
    require_specific_reviewers: {
        title: 'Require review by specified members',
        description: 'Require approval from all specified members.',
        evaluate: (rule, context) => {
            const reviewed = rule.users.filter((reviewer) => {
                if (reviewer === context.actor.id) {
                    // User can't review their own change request
                    return true;
                }

                return context.reviews.some((review) => review.reviewer.id === reviewer);
            });

            return reviewed.length !== rule.users.length
                ? `${rule.users.length - reviewed.length} / ${rule.users.length} expected reviews are missing`
                : true;
        },
    },
    require_one_of_specific_reviewers: {
        title: 'Require review by one of specified members',
        description: 'Require approval from any of the specified members.',
        evaluate: (rule, context) => {
            return !rule.users.some((reviewer) => {
                return context.reviews.some((review) => review.reviewer.id === reviewer);
            })
                ? 'At least one of the specified members must review the change request'
                : true;
        },
    },
    require_at_least_one_review: {
        title: 'Require at least one review',
        description: 'Require at least one review to be posted',
        evaluate: (_rule, context) => {
            return context.reviews.filter(
                (review) => review.reviewer.id !== GitBookUsers.GitbookAgent
            ).length === 0
                ? 'At least 1 review is required'
                : true;
        },
    },
    require_at_least_one_approved_review: {
        title: 'Require at least one approved review',
        description: 'Require at least one review approval to be posted',
        evaluate: (_rule, context) => {
            const approvedReviews = context.reviews.filter(
                (review) =>
                    review.reviewer.id !== GitBookUsers.GitbookAgent &&
                    review.status === ChangeRequestReviewStatus.Approved
            );

            return approvedReviews.length === 0 ? 'At least 1 approved review is required' : true;
        },
    },
    require_all_reviews_approved: {
        title: 'Require all reviews approved',
        description: 'Require all posted reviews to be approvals',
        evaluate: (_rule, context) => {
            const approved = context.reviews.filter(
                (review) => review.status === ChangeRequestReviewStatus.Approved
            );

            return approved.length !== context.reviews.length
                ? `${context.reviews.length - approved.length} reviews must be approved`
                : true;
        },
    },
    require_agent_review: {
        title: 'Require Docs Agent review',
        description: 'Require a review posted by the GitBook agent',
        evaluate: (_rule, context) => {
            return !context.reviews.some(
                (review) => review.reviewer.id === GitBookUsers.GitbookAgent
            )
                ? 'A review from the GitBook agent is required'
                : true;
        },
    },
    require_up_to_date_change_request: {
        title: 'Require up to date change request',
        description: 'Require the change request to be up to date with the primary content',
        evaluate: (_rule, context) => {
            return context.changeRequest.outdated
                ? 'Change request is outdated, it should be updated with the latest changes'
                : true;
        },
    },
    require_change_request_subject: {
        title: 'Require subject',
        description: 'Require the change request to have a subject',
        evaluate: (_rule, context) => {
            return context.changeRequest.subject === ''
                ? 'Change request is missing a subject'
                : true;
        },
    },
    require_change_request_description: {
        title: 'Require description',
        description: 'Require the change request to have a description',
        evaluate: (_rule, context) => {
            return !context.changeRequest.description
                ? 'Change request is missing a description'
                : true;
        },
    },
    require_author_to_merge: {
        title: 'Require the author to merge the change request',
        description: 'Only the author of the change request can merge',
        evaluate: (_rule, context) => {
            return context.actor.id !== context.changeRequest.createdBy.id
                ? 'Change request can only be merged by its author'
                : true;
        },
    },
    allow_bypass: {
        title: 'Allow specified actors to bypass rules',
        description: 'Specify people who are allowed to bypass the requirements to merge.',
        bypass: true,
        evaluate: (rule, context) => {
            return rule.users.includes(context.actor.id);
        },
    },
    custom: {
        title: 'Custom expression',
        description: 'Require the advanced expression to evaluate to true',
        evaluate: (rule, context) => {
            const runtime = new ExpressionRuntime();
            return runtime.evaluateBoolean(rule.expression, context)
                ? true
                : 'Custom merge rule expression evaluated to false';
        },
    },
};
